<template>
  <section class="shell-section">
    <div v-if="displayEvents.length || loadingMain" class="wow-grid" :class="{ 'wow-grid--archive': isArchiveMode }">
      <article
        v-for="(event, index) in displayEvents"
        :key="cardKey(event, index)"
        ref="cards"
        class="wow-card"
        :class="cardClass(index)"
        :style="cardStyle(index)"
        @click="handleCardAction(event)"
        @pointermove="handlePointer($event, index)"
        @pointerleave="handleLeave(index)"
        @touchstart.passive="handleTouchStart(index)"
        @touchend.passive="handleTouchEnd(index)"
      >
        <div class="wow-card__inner">
          <!-- Cursor / touch light reflection -->
          <div class="wow-card__reflection"></div>

          <!-- Shine sweep -->
          <div class="wow-card__shine-sweep" :class="{ 'wow-card__shine-sweep--active': shineActive[index] }"></div>

          <!-- Full-bleed image with scroll parallax -->
          <div class="wow-card__img-wrap">
            <img
              v-if="showImage(event)"
              class="wow-card__img"
              :src="imageUrl(event)"
              :alt="event.name"
              loading="lazy"
              :style="parallaxStyle(index)"
              @error="markImageError(event)"
            />
            <div v-else class="wow-card__placeholder">
              <div class="wow-card__placeholder-mark">{{ placeholderMark(event) }}</div>
            </div>
          </div>

          <!-- Cinematic veil -->
          <div class="wow-card__veil"></div>

          <!-- Edge light (top & bottom subtle border light) -->
          <div class="wow-card__edge-light"></div>

          <!-- Floating badges -->
          <div class="wow-card__badges">
            <span v-if="isArchiveMode" class="wow-card__badge wow-card__badge--archive">
              <i class="fas fa-history"></i>
              Archivo
            </span>
            <span class="wow-card__badge wow-card__badge--category">
              <i :class="categoryIcon(event)"></i>
              {{ categoryName(event) }}
            </span>
            <span class="wow-card__badge wow-card__badge--location">
              <i class="fas fa-map-marker-alt"></i>
              {{ cityName(event) }}
            </span>
          </div>

          <!-- Bottom content -->
          <div class="wow-card__bottom">
            <span class="wow-card__date-glass">
              {{ formatDate(event.start_date, event.access, event) }}
            </span>
            <h3 class="wow-card__title">{{ event.name }}</h3>
            <div v-if="isArchiveMode" class="wow-card__status-row">
              <span class="wow-card__status wow-card__status--archive">Evento cerrado</span>
              <span class="wow-card__status-note">Queda en el archivo FOMO</span>
            </div>
            <div class="wow-card__venue-reveal">
              <span class="wow-card__venue-name">{{ venueName(event) }}</span>
              <span class="wow-card__venue-sep">·</span>
              <span class="wow-card__venue-city">{{ cityName(event) }}</span>
            </div>
          </div>
        </div>
      </article>

      <article
        v-for="placeholder in loadingPlaceholders"
        :key="`placeholder-${placeholder}`"
        class="wow-card wow-card--visible wow-card--skeleton"
        aria-hidden="true"
      >
        <div class="wow-card__inner">
          <div class="wow-card__skeleton-media"></div>
          <div class="wow-card__veil wow-card__veil--skeleton"></div>
          <div class="wow-card__edge-light wow-card__edge-light--skeleton"></div>

          <div class="wow-card__badges wow-card__badges--skeleton">
            <span class="wow-card__skeleton-pill wow-card__skeleton-pill--wide"></span>
            <span class="wow-card__skeleton-pill"></span>
          </div>

          <div class="wow-card__bottom wow-card__bottom--skeleton">
            <span class="wow-card__skeleton-chip"></span>
            <span class="wow-card__skeleton-line wow-card__skeleton-line--title"></span>
            <span class="wow-card__skeleton-line wow-card__skeleton-line--title-short"></span>
            <span class="wow-card__skeleton-line wow-card__skeleton-line--meta"></span>
          </div>
        </div>
      </article>
    </div>

    <div v-else-if="showEmptyState" class="premium-empty shell-surface">
      {{ emptyStateCopy }}
    </div>

    <div v-if="next && !eventsProp && !infinite" class="load-more-wrap">
      <button v-if="!loadingMain" class="wow-card__load-more" type="button" @click="moreEvents">
        <span>Cargar más experiencias</span>
        <i class="fas fa-arrow-right"></i>
      </button>
      <div v-else class="load-more-wrap">
        <scaling-squares-spinner :size="60" color="#bec7d2" />
      </div>
    </div>

    <div v-if="shouldUseInfiniteLoader" ref="infiniteSentinel" class="wow-card__infinite-sentinel" aria-hidden="true">
      <span v-if="loadingMain">Cargando más experiencias...</span>
      <span v-else-if="next">Explorando más fechas...</span>
    </div>
  </section>
</template>

<script>
import { apiService } from '../common/api.services.js'
import { ScalingSquaresSpinner } from 'epic-spinners'
import { formatEventDate } from '@/common/event-timezone.js'

export default {
  name: 'EventCard',
  emits: ['events-count-updated'],
  components: { ScalingSquaresSpinner },

  props: {
    eventsProp: { type: Array, required: false, default: null },
    host: { required: false },
    show: { type: String, required: false, default: null },
    order: { type: String, required: false, default: null },
    pageSize: { type: Number, required: false, default: null },
    showEmptyState: { type: Boolean, required: false, default: true },
    interactive: { type: Boolean, required: false, default: true },
    cardMode: { type: String, required: false, default: 'live' },
    infinite: { type: Boolean, required: false, default: false },
    emptyStateCopy: {
      type: String,
      required: false,
      default: 'No encontramos eventos todavía. Vuelve pronto para descubrir nuevas experiencias.',
    },
    linkToHostPanel: { type: Boolean, required: false, default: false },
  },

  data() {
    return {
      events: [],
      next: null,
      loadingMain: false,
      failedImages: {},
      cardPointer: {},
      visibleCards: {},
      shineActive: {},
      scrollY: 0,
      cardOffsets: {},
      observer: null,
      infiniteObserver: null,
      ticking: false,
    }
  },

  computed: {
    displayEvents() {
      return Array.isArray(this.eventsProp) ? this.eventsProp : this.events
    },

    isArchiveMode() {
      return this.cardMode === 'archive'
    },

    loadingPlaceholders() {
      if (!this.loadingMain) {
        return []
      }

      const fallback = this.isArchiveMode ? 9 : 9
      const size = Math.min(Math.max(Number(this.pageSize) || fallback, 1), 9)
      return Array.from({ length: size }, (_, index) => index + 1)
    },

    shouldUseInfiniteLoader() {
      return this.infinite && !this.eventsProp && (this.loadingMain || Boolean(this.next))
    },
  },

  watch: {
    displayEvents() {
      this.$nextTick(() => {
        this.setupObserver()
        this.setupInfiniteObserver()
        this.ensureViewportFilled()
      })
    },

    next() {
      this.$nextTick(() => {
        this.setupInfiniteObserver()
        this.ensureViewportFilled()
      })
    },
  },

  created() {
    if (!this.eventsProp) {
      this.loadingMain = true
      this.getEvents()
    }
  },

  mounted() {
    this.handleScroll = () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.scrollY = window.scrollY || window.pageYOffset
          this.ticking = false
        })
        this.ticking = true
      }
    }
    this.handleResize = () => this.ensureViewportFilled()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })
    this.$nextTick(() => {
      this.setupObserver()
      this.setupInfiniteObserver()
      this.ensureViewportFilled()
    })
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
    if (this.observer) this.observer.disconnect()
    if (this.infiniteObserver) this.infiniteObserver.disconnect()
  },

  methods: {
    /* ── Scroll-triggered entry (IntersectionObserver) ── */
    setupObserver() {
      if (this.observer) this.observer.disconnect()
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.idx)
            if (entry.isIntersecting) {
              // Stagger the reveal
              setTimeout(() => {
                this.visibleCards = { ...this.visibleCards, [idx]: true }
              }, Math.min(idx * 100, 500))
              // Cache offset for parallax
              const rect = entry.target.getBoundingClientRect()
              this.cardOffsets = { ...this.cardOffsets, [idx]: rect.top + (window.scrollY || 0) }
            }
          })
        },
        { threshold: 0.1, rootMargin: '40px' }
      )
      if (this.$refs.cards) {
        this.$refs.cards.forEach((el, i) => {
          el.dataset.idx = i
          this.observer.observe(el)
        })
      }
    },

    setupInfiniteObserver() {
      if (this.infiniteObserver) {
        this.infiniteObserver.disconnect()
      }

      if (!this.shouldUseInfiniteLoader || !this.next || !this.$refs.infiniteSentinel) {
        return
      }

      this.infiniteObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.moreEvents()
            }
          })
        },
        { threshold: 0.01, rootMargin: '420px' }
      )

      this.infiniteObserver.observe(this.$refs.infiniteSentinel)
    },

    ensureViewportFilled() {
      if (!this.shouldUseInfiniteLoader || this.loadingMain || !this.next) {
        return
      }

      this.$nextTick(() => {
        const grid = this.$el && this.$el.querySelector('.wow-grid')
        if (!grid) {
          return
        }

        const rect = grid.getBoundingClientRect()
        if (rect.bottom < window.innerHeight * 1.28) {
          this.moreEvents()
        }
      })
    },

    /* ── Scroll parallax for image ── */
    parallaxStyle(index) {
      const offset = this.cardOffsets[index]
      if (offset === undefined) return {}
      const diff = this.scrollY - offset
      const shift = diff * 0.06 // subtle parallax factor
      return { transform: `translateY(${shift}px) scale(1.08)` }
    },

    /* ── 3D tilt ── */
    cardStyle(index) {
      const s = this.cardPointer[index]
      const base = { '--card-idx': index }
      if (!s || !s.active) {
        return { ...base, '--rx': '0deg', '--ry': '0deg', '--mx': '50%', '--my': '50%', '--shine': '0' }
      }
      const ry = ((s.x - 50) / 50) * 10
      const rx = ((s.y - 50) / 50) * -10
      return { ...base, '--rx': `${rx}deg`, '--ry': `${ry}deg`, '--mx': `${s.x}%`, '--my': `${s.y}%`, '--shine': '1' }
    },

    handlePointer(e, index) {
      if (e.pointerType === 'touch') return // handled separately
      const rect = e.currentTarget.getBoundingClientRect()
      this.cardPointer = {
        ...this.cardPointer,
        [index]: {
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
          active: true,
        },
      }
    },

    handleLeave(index) {
      const prev = this.cardPointer[index]
      this.cardPointer = {
        ...this.cardPointer,
        [index]: { x: prev ? prev.x : 50, y: prev ? prev.y : 50, active: false },
      }
    },

    /* ── Mobile touch effects ── */
    handleTouchStart(index) {
      // Trigger shine sweep on tap
      this.shineActive = { ...this.shineActive, [index]: false }
      // Force reflow to restart animation
      this.$nextTick(() => {
        this.shineActive = { ...this.shineActive, [index]: true }
      })
    },

    handleTouchEnd(index) {
      setTimeout(() => {
        this.shineActive = { ...this.shineActive, [index]: false }
      }, 600)
    },

    cardKey(event, index) {
      return `${event && (event.slug || event.id || event.name || 'event')}-${index}`
    },

    cardClass(index) {
      return {
        'wow-card--visible': this.visibleCards[index],
        'wow-card--archive': this.isArchiveMode,
        'wow-card--static': !this.interactive,
      }
    },

    /* ── Helpers ── */
    normalizeLabel(value) {
      const compact = value ? String(value).replace(/\s+/g, ' ').trim() : ''
      if (!compact || compact !== compact.toUpperCase()) return compact
      const softWords = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'y', 'o', 'en', 'al'])
      return compact.toLowerCase().split(' ')
        .map((w, i) => (softWords.has(w) && i !== 0 ? w : `${w.charAt(0).toUpperCase()}${w.slice(1)}`))
        .join(' ')
    },

    formatDate(dateString, access, event) {
      if (access === 4) return 'Multiples fechas'
      return formatEventDate(dateString, event || {}, { day: '2-digit', month: 'short', year: 'numeric' })
    },

    categoryName(event) { return event.category && event.category.name ? event.category.name : 'Evento' },
    categoryIcon(event) { return event.category && event.category.icon ? event.category.icon : 'fas fa-music' },
    cityName(event) {
      return event.venue && event.venue.city && event.venue.city.name
        ? this.normalizeLabel(event.venue.city.name) : 'Ciudad por confirmar'
    },
    venueName(event) {
      return event.venue && event.venue.name
        ? this.normalizeLabel(event.venue.name) : 'Venue por confirmar'
    },
    imageKey(event) { return event.slug || event.id || event.name },

    resolveMediaCandidates(value) {
      if (!value) return []
      const origin = window.location.origin
      const local = ['http://127.0.0.1:8000', 'http://localhost:8000']
      const norm = (c) => c.replace(/^\/media\//, '').replace(/^.*\/media\//, '').replace(/^media\//, '').replace(/^\//, '')
      if (value.startsWith('data:') || value.startsWith('blob:')) return [value]
      if (/^https?:\/\//i.test(value)) {
        try {
          const p = new URL(value)
          if (!p.pathname.includes('/media/')) return [value]
          const np = norm(p.pathname)
          return [...new Set([value, `${origin}/media/${np}`, ...local.map((b) => `${b}/media/${np}`), `https://gingopolisdjango.blob.core.windows.net/fomoapp/media/${np}`])]
        } catch (e) { return [value] }
      }
      const np = norm(value)
      if (value.startsWith('/media/') || value.startsWith('media/') || !value.startsWith('/')) {
        return [...new Set([`${origin}/media/${np}`, ...local.map((b) => `${b}/media/${np}`), `https://gingopolisdjango.blob.core.windows.net/fomoapp/media/${np}`])]
      }
      return [`${origin}${value}`]
    },

    imageCandidates(event) {
      return [event.banner_url, event.image_url, event.cover_url, event.thumbnail]
        .filter(Boolean).flatMap((v) => this.resolveMediaCandidates(v))
    },
    imageUrl(event) {
      const f = this.failedImages[this.imageKey(event)] || []
      return this.imageCandidates(event).find((c) => !f.includes(c)) || ''
    },
    showImage(event) { return Boolean(this.imageUrl(event)) },
    markImageError(event) {
      const k = this.imageKey(event), f = this.failedImages[k] || [], a = this.imageUrl(event)
      this.failedImages = { ...this.failedImages, [k]: a ? [...f, a] : f }
    },
    placeholderMark(event) {
      return (event && event.name ? event.name.trim() : 'Fomo').slice(0, 2).toUpperCase()
    },
    openEvent(slug, type, id) {
      if (this.linkToHostPanel) {
        this.$router.push({ name: 'EventHostView', params: { slug: id || slug } })
        return
      }
      if (type === 4) this.$router.push({ name: 'host', params: { host: slug } })
      else this.$router.push({ name: 'event', params: { slug } })
    },

    handleCardAction(event) {
      if (!this.interactive) {
        return
      }

      this.$emit('card-click', event)
      this.openEvent(event.slug, event.access, event.id)
    },

    moreEvents() {
      if (this.loadingMain || !this.next) {
        return
      }

      this.loadingMain = true
      this.getEvents()
    },

    getEvents() {
      let ep = '/api/events/?type=1'
      const qp = []
      if (this.show) qp.push(`show=${encodeURIComponent(this.show)}`)
      if (this.order) qp.push(`order=${encodeURIComponent(this.order)}`)
      if (this.pageSize) qp.push(`page_size=${encodeURIComponent(this.pageSize)}`)
      if (qp.length) ep = `${ep}&${qp.join('&')}`
      if (this.host !== null && this.host !== undefined) ep = `/api/events/?type=3&host=${this.host}`
      if (this.next) ep = this.next
      apiService(ep).then((data) => {
        const r = data && data.results ? data.results : []
        this.events = this.events.concat(r)
        this.next = data && data.next ? data.next : null
        this.$emit('events-count-updated', data && typeof data.count === 'number' ? data.count : this.events.length)
      }).finally(() => {
        this.loadingMain = false
      })
    },
  },
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════
   WOW CARD v5 — Best in Market
   Cross-platform: Desktop 3D + Mobile Touch + Scroll Parallax
   ═══════════════════════════════════════════════════ */

.wow-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

/* ── Card shell ── */
.wow-card {
  --wow-card-radius: 24px;
  position: relative;
  border-radius: var(--wow-card-radius);
  cursor: pointer;
  perspective: 900px;
  /* Hidden until IntersectionObserver triggers */
  opacity: 0;
  transform: translateY(32px) scale(0.96);
  filter: blur(6px);
  transition: opacity 0s, transform 0s, filter 0s; /* no transition until visible */
}

.wow-card--static {
  cursor: default;
}

.wow-card--archive {
  --archive-glow: rgba(108, 129, 168, 0.12);
}

.wow-card--visible {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
  transition:
    opacity 600ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 700ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 600ms ease;
}

/* ── 3D tilt inner ── */
.wow-card__inner {
  position: relative;
  border-radius: var(--wow-card-radius);
  overflow: hidden;
  isolation: isolate;
  aspect-ratio: 3 / 4;
  transform-style: preserve-3d;
  transform: rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 350ms ease;
  will-change: transform;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.wow-card:hover .wow-card__inner {
  box-shadow:
    0 28px 56px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.08),
    0 0 80px -20px rgba(139, 92, 246, 0.08);
}

.wow-card--archive .wow-card__inner {
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.34),
    0 0 0 1px rgba(190, 205, 226, 0.06),
    0 0 72px -28px var(--archive-glow);
}

/* ── Mobile press effect ── */
.wow-card:active .wow-card__inner {
  transform: scale(0.97);
  transition: transform 120ms ease;
}

/* ── Light reflection (follows cursor on desktop) ── */
.wow-card__reflection {
  position: absolute;
  inset: 0;
  z-index: 5;
  border-radius: inherit;
  background: radial-gradient(
    500px circle at var(--mx, 50%) var(--my, 50%),
    rgba(255, 255, 255, 0.1),
    transparent 55%
  );
  opacity: var(--shine, 0);
  transition: opacity 350ms ease;
  pointer-events: none;
  mix-blend-mode: overlay;
}

/* ── Shine sweep (both hover + touch) ── */
.wow-card__shine-sweep {
  position: absolute;
  inset: 0;
  z-index: 6;
  border-radius: inherit;
  background: linear-gradient(
    105deg,
    transparent 0%,
    transparent 38%,
    rgba(255, 255, 255, 0.04) 42%,
    rgba(255, 255, 255, 0.14) 50%,
    rgba(255, 255, 255, 0.04) 58%,
    transparent 62%,
    transparent 100%
  );
  background-size: 300% 100%;
  background-position: 200% 0;
  pointer-events: none;
}

/* Desktop: trigger on hover */
.wow-card:hover .wow-card__shine-sweep {
  animation: shineSweep 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

/* Mobile: trigger on touch via class */
.wow-card__shine-sweep--active {
  animation: shineSweep 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards !important;
}

@keyframes shineSweep {
  from { background-position: 200% 0; }
  to { background-position: -100% 0; }
}

/* ── Image wrapper (clip for parallax) ── */
.wow-card__img-wrap {
  position: absolute;
  inset: -12%;
  border-radius: inherit;
  overflow: hidden;
}

.wow-card__img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  animation: kenBurns 24s ease-in-out alternate infinite;
  transition: filter 500ms ease;
  will-change: transform;
}

.wow-card:hover .wow-card__img {
  animation-play-state: paused;
  filter: brightness(1.12) saturate(1.15);
}

.wow-card--archive .wow-card__img {
  filter: saturate(0.72) brightness(0.88);
}

.wow-card--archive:hover .wow-card__img,
.wow-card--archive:active .wow-card__img {
  animation-play-state: running;
  filter: saturate(0.82) brightness(0.94);
}

@keyframes kenBurns {
  0%   { transform: scale(1) translate(0, 0); }
  25%  { transform: scale(1.06) translate(-1.2%, -0.8%); }
  50%  { transform: scale(1.04) translate(0.8%, -0.4%); }
  75%  { transform: scale(1.07) translate(-0.6%, -1.2%); }
  100% { transform: scale(1.08) translate(-0.4%, -1%); }
}

/* ── Placeholder ── */
.wow-card__placeholder {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at 30% 40%, rgba(139, 92, 246, 0.06), transparent 50%),
    linear-gradient(135deg, #15112a 0%, #0d0e18 100%);
}

.wow-card__placeholder-mark {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.08);
  letter-spacing: 0.1em;
}

/* ── Cinematic veil ── */
.wow-card__veil {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  z-index: 1;
  background:
    linear-gradient(180deg,
      rgba(0, 0, 0, 0.2) 0%,
      transparent 22%,
      transparent 40%,
      rgba(0, 0, 0, 0.2) 55%,
      rgba(0, 0, 0, 0.72) 80%,
      rgba(0, 0, 0, 0.9) 100%
    );
  pointer-events: none;
  transition: background 400ms ease;
}

.wow-card:hover .wow-card__veil,
.wow-card:active .wow-card__veil {
  background:
    linear-gradient(180deg,
      rgba(0, 0, 0, 0.12) 0%,
      transparent 22%,
      transparent 40%,
      rgba(0, 0, 0, 0.28) 55%,
      rgba(0, 0, 0, 0.78) 80%,
      rgba(0, 0, 0, 0.94) 100%
    );
}

/* ── Edge light (subtle border glow top+bottom) ── */
.wow-card__edge-light {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: border-color 400ms ease;
}

.wow-card:hover .wow-card__edge-light,
.wow-card:active .wow-card__edge-light {
  border-color: rgba(255, 255, 255, 0.14);
}

.wow-card__edge-light::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 20%;
  right: 20%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  opacity: 0;
  transition: opacity 400ms ease;
}

.wow-card:hover .wow-card__edge-light::before,
.wow-card:active .wow-card__edge-light::before {
  opacity: 1;
}

/* ── Badges ── */
.wow-card__badges {
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  transform: translateZ(24px);
}

.wow-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border-radius: 999px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f0eef5;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 300ms ease, background 300ms ease, border-color 300ms ease;
}

.wow-card__badge--category { border-color: rgba(141, 89, 255, 0.18); }
.wow-card__badge--location { border-color: rgba(248, 216, 139, 0.14); }
.wow-card__badge--archive {
  border-color: rgba(142, 184, 255, 0.18);
  background: rgba(15, 24, 42, 0.44);
}

.wow-card:hover .wow-card__badge {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateZ(32px);
}

/* ── Bottom content ── */
.wow-card__bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding: 0 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transform: translateZ(18px);
}

/* ── Date — Liquid Glass ── */
.wow-card__date-glass {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 7px 16px;
  border-radius: 999px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(32px) saturate(200%);
  -webkit-backdrop-filter: blur(32px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    inset 0 -1px 0 rgba(255, 255, 255, 0.04),
    0 4px 16px rgba(0, 0, 0, 0.15);
  transition: background 300ms ease, border-color 300ms ease, box-shadow 300ms ease, transform 300ms ease;
}

.wow-card:hover .wow-card__date-glass,
.wow-card:active .wow-card__date-glass {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.24);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(255, 255, 255, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

/* ── Title ── */
.wow-card__title {
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: #ffffff;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.5);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  transition: transform 400ms cubic-bezier(0.22, 1, 0.36, 1);
}

.wow-card__status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.wow-card__status {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(240, 244, 255, 0.92);
}

.wow-card__status--archive {
  background: rgba(92, 117, 158, 0.18);
  border-color: rgba(149, 182, 232, 0.18);
}

.wow-card__status-note {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(212, 220, 236, 0.58);
}

.wow-card:hover .wow-card__title,
.wow-card:active .wow-card__title {
  transform: translateY(-2px);
}

/* ── Venue — always visible, enhances on hover/touch ── */
.wow-card__venue-reveal {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.5;
  transform: translateY(4px);
  transition: opacity 400ms ease, transform 400ms cubic-bezier(0.22, 1, 0.36, 1);
}

.wow-card:hover .wow-card__venue-reveal,
.wow-card:active .wow-card__venue-reveal {
  opacity: 1;
  transform: translateY(0);
}

.wow-card__venue-name {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.74rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.wow-card__venue-sep {
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.8rem;
}

.wow-card__venue-city {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

/* ── Load More ── */
.wow-card__load-more {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #bec7d2;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 250ms ease;
}

.wow-card__load-more:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
  transform: translateY(-2px);
}

.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.wow-card__infinite-sentinel {
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  color: rgba(196, 206, 224, 0.56);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.wow-card--skeleton {
  cursor: default;
  pointer-events: none;
}

.wow-card--skeleton .wow-card__inner {
  background: linear-gradient(180deg, rgba(12, 16, 24, 0.96), rgba(8, 10, 16, 0.98));
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22);
}

.wow-card__skeleton-media,
.wow-card__skeleton-chip,
.wow-card__skeleton-line,
.wow-card__skeleton-pill {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
}

.wow-card__skeleton-media::after,
.wow-card__skeleton-chip::after,
.wow-card__skeleton-line::after,
.wow-card__skeleton-pill::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
  animation: wowSkeletonSweep 1.45s ease-in-out infinite;
}

.wow-card__skeleton-media {
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.wow-card__badges--skeleton {
  justify-content: flex-start;
}

.wow-card__skeleton-pill {
  height: 28px;
  width: 92px;
  border-radius: 999px;
}

.wow-card__skeleton-pill--wide {
  width: 122px;
}

.wow-card__bottom--skeleton {
  gap: 10px;
}

.wow-card__skeleton-chip {
  width: 126px;
  height: 30px;
  border-radius: 999px;
}

.wow-card__skeleton-line {
  display: block;
  height: 16px;
  border-radius: 999px;
}

.wow-card__skeleton-line--title {
  width: 82%;
  height: 20px;
}

.wow-card__skeleton-line--title-short {
  width: 58%;
  height: 20px;
}

.wow-card__skeleton-line--meta {
  width: 68%;
  height: 14px;
}

.wow-card__veil--skeleton {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.22) 58%, rgba(0, 0, 0, 0.62) 100%);
}

.wow-card__edge-light--skeleton {
  border-color: rgba(255, 255, 255, 0.04);
}

@keyframes wowSkeletonSweep {
  100% {
    transform: translateX(100%);
  }
}

/* ════════════════════════════════
   Responsive
   ════════════════════════════════ */
@media (max-width: 1100px) {
  .wow-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
}

@media (max-width: 640px) {
  .wow-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    max-width: 420px;
    margin: 0 auto;
  }

  /* Disable 3D tilt on mobile for performance */
  .wow-card__inner {
    transform: none !important;
  }

  .wow-card__img-wrap {
    inset: 0;
    clip-path: inset(0 round var(--wow-card-radius));
    -webkit-mask-image: -webkit-radial-gradient(white, black);
  }

  .wow-card__img {
    animation: none;
    transform: none !important;
  }

  /* Always show venue on mobile */
  .wow-card__venue-reveal {
    opacity: 0.7;
    transform: translateY(0);
  }

  .wow-card__title {
    font-size: 1.35rem;
  }

  .wow-card__status-row {
    gap: 6px;
  }
}
</style>
